import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import fscreen from 'fscreen';

export interface FullScreenHandle {
  active: boolean;
  enter: () => void;
  exit: () => void;
  node: React.MutableRefObject<HTMLDivElement | null>;
}

export interface FullScreenProps {
  handle: FullScreenHandle;
  children: any;
  onChange?: (state: boolean, handle: FullScreenHandle) => void;
  className?: string;
}

export const useFullScreenHandle = (
  topLevelNode?: HTMLElement | null,
): FullScreenHandle => {
  const [active, setActive] = useState<boolean>(false);
  const node = useRef<HTMLDivElement | null>(null);

  const currentNode = topLevelNode ?? node.current;

  useEffect(() => {
    const handleChange = () => {
      setActive(fscreen.fullscreenElement === currentNode);
    };
    fscreen.addEventListener('fullscreenchange', handleChange);
    return () => fscreen.removeEventListener('fullscreenchange', handleChange);
  }, [currentNode]);

  const enter = useCallback(() => {
    if (fscreen.fullscreenElement && currentNode) {
      fscreen.exitFullscreen();
      fscreen.requestFullscreen(currentNode);
    } else if (currentNode) {
      return fscreen.requestFullscreen(currentNode);
    }
  }, [currentNode]);

  const exit = useCallback(() => {
    if (fscreen.fullscreenElement === currentNode) {
      return fscreen.exitFullscreen();
    }
  }, [currentNode]);

  return useMemo(
    () => ({
      active,
      enter,
      exit,
      node,
    }),
    [active, enter, exit],
  );
};

export const FullScreen: React.FC<FullScreenProps> = ({
  handle,
  onChange,
  children,
  className,
}) => {
  const classNames = [];

  if (className) {
    classNames.push(className);
  }

  classNames.push('fullscreen');

  if (handle.active) {
    classNames.push('fullscreen-enabled');
  }

  useEffect(() => {
    if (onChange) {
      onChange(handle.active, handle);
    }
  }, [handle, handle.active, onChange]);

  return (
    <div
      className={classNames.join(' ')}
      ref={handle.node}
      style={handle.active ? { height: '100%', width: '100%' } : undefined}
    >
      {children}
    </div>
  );
};
